/**
 * utils/paymentSplit.js
 * Two-bucket payment split (Barang / Tagihan & Potongan) — pure, immutable.
 *
 * `txnTotal(txn)` is already secretly two numbers added together: `goodsTotal(txn)` (the
 * goods/"Subtotal Barang" bucket) and `txnTotal(txn) - goodsTotal(txn)` (the net "Tagihan &
 * Potongan" bucket — `sumCostItemsRaisingTotal - sumPotonganCostItems`). This module lets a
 * payment record which bucket it actually paid off, without ever touching the real
 * Total/Sisa/Lunas math (`txnTotal`/`txnSisa`/`txnStatus` in `txnStatus.js` are completely
 * unaware this module exists). `biaya`-type cost items never touch `txnTotal` at all, so they
 * are NOT part of either bucket here.
 *
 * A payment row gains two new OPTIONAL fields, `splitBarang`/`splitTagihanPotongan` — named
 * after the bucket's own display name, not the cost-item `type` vocabulary, to avoid colliding
 * with `type: "tagihan"`. Presence is semantically load-bearing: a row carries BOTH fields iff
 * it was recorded (or later corrected) with a deliberate split. Absence means "frozen, 100%
 * Barang" — resolved by `paymentSplit()` below, never by a stored default. No storage
 * migration/`NORM_VERSION` bump exists for this — see the root CLAUDE.md's own entry.
 */
import { goodsTotal, txnTotal, txnStatus } from "./txnStatus";
import { fmtQty } from "./idGenerators";

/** The "Subtotal Barang" bucket's owed amount — identical to goodsTotal, exported under this
 *  module's own bucket vocabulary for callers that think in buckets, not raw totals. */
export function barangOwed(txn) {
  return goodsTotal(txn);
}

/** The "Tagihan & Potongan" bucket's owed amount — can be NEGATIVE (a Potongan outweighing a
 *  Tagihan is an ordinary, common transaction shape, not an edge case) or exactly zero (no
 *  cost items at all, or a net cancel-out). Both mean "nothing owed in this bucket." */
export function tagihanPotonganOwed(txn) {
  return txnTotal(txn) - goodsTotal(txn);
}

/** The live gate behind the "no live Tagihan & Potongan bucket" full-revert case — true only
 *  when there is genuinely something owed in this bucket right now. Recalculated on every call,
 *  never cached/fixed at creation. */
export function hasTagihanPotonganBucket(txn) {
  return tagihanPotonganOwed(txn) > 0;
}

/** A narrower, ITEM-presence-based gate for the per-payment split line only (2nd-round
 *  follow-up fix) — true whenever a real tagihan/potongan-type cost item currently exists on
 *  the transaction, regardless of the bucket's NET sign. `hasTagihanPotonganBucket` above
 *  (net > 0) stays exactly as-is, still the gate for the two progress bars via
 *  `shouldShowBucketBars` below — a transaction with a tagihan AND a larger potongan (net <= 0)
 *  should still show a real payment's split line (real cost items exist, a real split may have
 *  been recorded against them) even though the two bars correctly stay hidden (nothing is
 *  currently owed in the bucket). `biaya`-type items are deliberately excluded — they've never
 *  been part of either bucket anywhere in this feature. Known, pre-existing-shaped edge case
 *  (not new here — `hasTagihanPotonganBucket` already behaves this way): this reads the
 *  transaction's CURRENT `costItems` array, so if every tagihan/potongan item is later deleted
 *  outright (not just netted to zero by another item), this goes false again and the split line
 *  disappears — even if a payment still carries real, persisted split data from when the item
 *  existed. */
export function hasTagihanPotonganItems(txn) {
  // A Pembelian's tagihan is stored as "tagihanSupplier" (labeled plain "Tagihan" since
  // 2026-10-11) — it belongs to this bucket exactly like a sale's "tagihan"; leaving it out hid a
  // purchase's per-payment split line even though its bucket was live.
  return (txn.costItems || []).some(
    (c) => c.type === "tagihan" || c.type === "tagihanSupplier" || c.type === "potongan"
  );
}

/** The ONE place the legacy-fallback rule lives: a payment row with both split fields present
 *  (as real numbers) reports its own real split; any other row (genuinely predates a bucket
 *  ever existing, or was recorded when there was never one) is frozen — 100% counted as
 *  Barang, permanently, until someone deliberately edits that row's own split fields. */
export function paymentSplit(p) {
  if (typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number") {
    return { barang: p.splitBarang, tagihanPotongan: p.splitTagihanPotongan };
  }
  return { barang: p.amount, tagihanPotongan: 0 };
}

/** The shared "how does this payment's split render as one line of text" helper (Brief 3) —
 *  used by BOTH TransactionDetailModal's and BayarTagihanModal's read-only payment lists, so
 *  the two can never silently drift apart. Delegates to paymentSplit() for the actual
 *  split-vs-frozen determination rather than re-deriving that check a second time. */
export function paymentSplitLine(p) {
  if (typeof p.splitBarang !== "number" || typeof p.splitTagihanPotongan !== "number") {
    // A row predating the bucket always falls back to 100% Barang (paymentSplit()'s own
    // fallback rule, never the other way around), so the symmetric "Semua untuk Tagihan &
    // Potongan" case can never occur here — no symmetric fallback string is needed.
    return `Barang: Rp ${fmtQty(p.amount)}`;
  }
  const { barang, tagihanPotongan } = paymentSplit(p);
  return `Barang: Rp ${fmtQty(barang)} · Tagihan & Potongan: Rp ${fmtQty(tagihanPotongan)}`;
}

export function barangPaid(txn) {
  return (txn.payments || []).reduce((s, p) => s + paymentSplit(p).barang, 0);
}

export function tagihanPotonganPaid(txn) {
  return (txn.payments || []).reduce((s, p) => s + paymentSplit(p).tagihanPotongan, 0);
}

export function barangSisa(txn) {
  return barangOwed(txn) - barangPaid(txn);
}

export function tagihanPotonganSisa(txn) {
  return tagihanPotonganOwed(txn) - tagihanPotonganPaid(txn);
}

/** Same formula shape as txnStatus.js's own pctPaid — owed<=0 reads as fully paid (100)
 *  rather than dividing by zero or a negative number. */
function pctOf(owed, sisa) {
  return owed > 0 ? Math.min(100, Math.round(((owed - sisa) / owed) * 100)) : 100;
}

export function barangPctPaid(txn) {
  return pctOf(barangOwed(txn), barangSisa(txn));
}

export function tagihanPotonganPctPaid(txn) {
  return pctOf(tagihanPotonganOwed(txn), tagihanPotonganSisa(txn));
}

/** Bar-visibility rule for both new progress bars: gated on the transaction's OVERALL derived
 *  status only — never per-bucket 100%, since one bucket can legitimately be fully paid while
 *  the transaction as a whole is still Piutang/Utang. */
export function shouldShowBucketBars(txn) {
  return hasTagihanPotonganBucket(txn) && txnStatus(txn) !== "LUNAS";
}

/** A payment row's typed split must sum to exactly its own Jumlah, both parts non-negative —
 *  the live checkmark/error-text rule on every split-entry surface. Both split arguments must
 *  be REAL numbers (typeof-checked) — a genuinely blank/null field (never typed, or cleared
 *  mid-edit) must never be silently coerced to 0 here, even when the OTHER field alone happens
 *  to sum to the full amount: that would show a false "✓ Sesuai" checkmark and let Simpan
 *  enable, while the caller's own outgoing-patch gate (correctly typeof-based) would then omit
 *  BOTH split fields from what's actually saved — a real, silent data-loss bug, found during
 *  review of the parseCurrencyInput null-vs-0 fix (a blank field used to coerce to a real 0 at
 *  the input layer itself, which happened to keep this function and the outgoing-patch gate in
 *  agreement; once blank became genuinely null, this coercion here caused the two to disagree).
 *  `amount` keeps its own plain `Number(amount)` coercion — only the two split fields carry the
 *  "presence signals a deliberate split" semantic that requires the stricter check. */
export function validateSplitSum(amount, splitBarang, splitTagihanPotongan) {
  if (typeof splitBarang !== "number" || typeof splitTagihanPotongan !== "number") return false;
  if (!(splitBarang >= 0) || !(splitTagihanPotongan >= 0)) return false;
  return splitBarang + splitTagihanPotongan === Number(amount);
}

export const BUCKET_LABEL = { barang: "Barang", tagihanPotongan: "Tagihan & Potongan" };

/**
 * Whole-array check: can either bucket's total split-paid exceed what it currently owes?
 * `owed` is clamped with `Math.max(0, owed)` — a bucket with a negative or zero owed amount
 * both mean "nothing owed," so neither can ever be "over-allocated" just because
 * `paymentSplit()`'s legacy fallback correctly puts 0 there. This is Check 1's core (point
 * 5/6) AND doubles as point 3's "can never push a bucket over what it owes" entry check when
 * applied to a single new row's resulting contribution.
 *
 * @param {{lines, costItems, payments}} txnLike
 * @returns {{ok: boolean, violations: {key, label, owed, paid}[]}}
 */
export function checkBucketOverAllocation(txnLike) {
  const buckets = [
    { key: "barang", label: BUCKET_LABEL.barang, owed: barangOwed(txnLike), paid: barangPaid(txnLike) },
    { key: "tagihanPotongan", label: BUCKET_LABEL.tagihanPotongan, owed: tagihanPotonganOwed(txnLike), paid: tagihanPotonganPaid(txnLike) },
  ];
  const violations = buckets.filter((b) => b.paid > Math.max(0, b.owed));
  return { ok: violations.length === 0, violations };
}

/**
 * Point 6's ONE dynamic message template — shared by App.js's console.warn text,
 * `BayarTagihanModal`'s own inline over-allocation hint (its add-only flow's own Check 1
 * surface), and (the single-toggle redesign) `TransactionFormModal`'s own state-2
 * over-allocation message (see that file's own header comment — the toggle-agnostic
 * replacement for `bucketMessage`'s cases 1/2 once a bucket's own per-toggle completeness
 * check no longer applies). `owed <= 0` (not `=== 0`) shares the "nothing to pay"
 * wording with a net-negative bucket (a discount outweighing charges) — a negative Rupiah
 * amount must never be printed here. The wording itself is deliberately cause-neutral
 * ("setelah diubah") in BOTH branches — `owed` can hit zero/negative from a plain value edit
 * (e.g. an existing Potongan increased against an existing Tagihan) just as easily as from an
 * outright deletion, and the message must never assume which one actually happened.
 *
 * The `owed > 0` branch names the excess ("kelebihan Rp X"), matching `bucketMessage`'s own
 * case-1 wording for the identical class of problem — added so a real user reading this text
 * (via BayarTagihanModal's or TransactionFormModal's own inline hint) gets the same level of
 * detail regardless of which of the two message functions happens to be showing it; the
 * `owed <= 0` branch has no excess figure to add (paid is simply "not needed at all").
 */
export function bucketOverAllocationMessage(v) {
  const mid = v.owed <= 0
    ? `tapi setelah diubah, ${v.label} sekarang tidak perlu dibayar sama sekali.`
    : `tapi setelah diubah, ${v.label} cuma perlu Rp ${fmtQty(v.owed)}, kelebihan Rp ${fmtQty(v.paid - v.owed)}.`;
  return `${v.label} sudah dibayar Rp ${fmtQty(v.paid)}, ${mid} Sesuaikan pembayaran yang sudah tercatat untuk ${v.label} sebelum menyimpan.`;
}

/**
 * Check 2 (point 4) — per-bucket completeness against a Lunas/Belum-Lunas toggle intent.
 * Lunas requires an EXACT match to what's currently owed; Belum Lunas requires STRICTLY LESS
 * (never equal or over) — the same exact-match/strictly-less shape `payments.js`'s
 * `validatePayments` already uses for the single, whole-transaction intent, just scoped to one
 * bucket at a time so both can be checked (and can both fail) independently.
 *
 * `owed` is clamped with the SAME `Math.max(0, owed)` rule `checkBucketOverAllocation` already
 * uses (a real bug caught during Brief 2 plan review, before Brief 2's own code existed):
 * without the clamp, a bucket driven to `owed <= 0` (a Potongan outweighing a Tagihan, or the
 * item simply removed) made BOTH toggle states permanently unsatisfiable — Lunas needs
 * `paid === owed` exactly (never true once `owed` is negative and `paid` is a real amount),
 * Belum Lunas needs `paid < owed` (`paid >= a non-positive number` is always true once
 * `paid >= 0`) — locking Simpan forever for a transaction that in fact owes nothing in that
 * bucket, for ANY future edit, not just a money-related one. Once `owed <= 0`, the bucket owes
 * nothing regardless of which toggle is selected — skip the check entirely.
 */
export function validateBucketCompleteness(label, paid, owed, lunasIntent) {
  const effectiveOwed = Math.max(0, owed);
  if (effectiveOwed === 0) return { ok: true, error: null };
  if (lunasIntent && paid !== effectiveOwed) {
    return { ok: false, error: `Status Pembayaran ${label}: Lunas harus berjumlah tepat sama dengan Sisa ${label}` };
  }
  if (!lunasIntent && paid >= effectiveOwed) {
    return { ok: false, error: `Status Pembayaran ${label}: Belum Lunas harus berjumlah kurang dari Sisa ${label}` };
  }
  return { ok: true, error: null };
}

/**
 * The single per-bucket inline message for TransactionFormModal's Edit Transaksi UI (the
 * blocking-modal/two-pass-gate redesign) — replaces Check 1's modal + Check 2's separate
 * stale-exempted hint with ONE message per bucket, decided purely from paid-vs-owed and the
 * toggle, never from whether a row is "old" or "new." Four cases, in priority order:
 *
 * 1. paid > owed, owed still positive: over-allocated, bucket still owes something.
 * 2. paid > 0, owed <= 0: over-allocated, bucket now owes nothing at all.
 * 3. paid === owed (genuinely fully paid) but the toggle says Belum Lunas.
 * 4. paid < owed but the toggle says Lunas.
 *
 * Cases 1/2 ALWAYS win over 3/4 — structurally, not via an explicit priority check: 1/2 require
 * `paid > effectiveOwed` strictly, while 3 requires `paid === effectiveOwed` and 4 requires
 * `paid < effectiveOwed`, so the four conditions are mutually exclusive by construction.
 * Mathematically, whenever paid > owed, a naive read of Check 2's own old exact-match
 * (`paid !== owed`) / strictly-less (`paid >= owed`) conditions would ALSO fire regardless of
 * toggle state — this function never shows that second, redundant message; only ONE of the
 * four texts is ever returned for a given (paid, owed, lunasIntent).
 *
 * Toggle-switching advice ("Ubah ke Lunas/Belum Lunas") is correct and safe ONLY in cases 3/4,
 * where switching genuinely resolves the problem on its own — cases 1/2 never suggest it, since
 * neither toggle state fixes an over-allocation; that would be actively misleading.
 *
 * Returns the message string, or `null` when the bucket is in a valid state.
 */
export function bucketMessage(label, paid, owed, lunasIntent) {
  const effectiveOwed = Math.max(0, owed);
  if (paid > effectiveOwed) {
    if (effectiveOwed > 0) {
      return `${label} sudah dibayar Rp ${fmtQty(paid)}, tapi setelah diubah, ${label} cuma perlu Rp ${fmtQty(effectiveOwed)}, kelebihan Rp ${fmtQty(paid - effectiveOwed)}. Sesuaikan pembayaran yang sudah tercatat untuk ${label}.`;
    }
    return `${label} sudah dibayar Rp ${fmtQty(paid)}, tapi setelah diubah, ${label} sekarang tidak perlu dibayar sama sekali. Sesuaikan pembayaran yang sudah tercatat untuk ${label}.`;
  }
  if (effectiveOwed === 0) return null;
  if (paid === effectiveOwed && !lunasIntent) {
    return `Status Pembayaran ${label}: sudah dibayar penuh Rp ${fmtQty(effectiveOwed)}. Ubah ke Lunas untuk melanjutkan.`;
  }
  if (paid < effectiveOwed && lunasIntent) {
    return `Status Pembayaran ${label}: sudah dibayar Rp ${fmtQty(paid)} dari Rp ${fmtQty(effectiveOwed)}, kurang Rp ${fmtQty(effectiveOwed - paid)}. Ubah ke Belum Lunas, atau lengkapi pembayaran hingga Rp ${fmtQty(effectiveOwed)}.`;
  }
  return null;
}
